import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CardShell from '../../components/CardShell';
import SegmentedToggle from '../../components/SegmentedToggle';
import { useP } from '../../i18n/useP';
import './Sales.css';

/* SAISONNALITÉ DE LA FACTURATION (refaite le 2026-10-10 au format des autres
   pages, proto `WIP/protos/facturation-homogene.html`) : les années retenues
   (dans l'entête de la carte), leur moyenne, la part de chaque mois et
   l'INDICE base 100, la même échelle que les profils de saisonnalité des
   Prévisions. Une catégorie se retrouve d'une année à l'autre par son libellé. */
function formatCHF(value, unit = 'kchf') {
  if (value === null || value === undefined || Math.abs(value) < (unit === 'kchf' ? 500 : 0.5)) return '–';
  return Math.round(unit === 'kchf' ? value / 1000 : value).toLocaleString('de-CH');
}
const pct = (v) => `${(v * 100).toFixed(1)} %`;

export default function BillingSeasonalityTab({
  dataPrevYear, dataPrev2Year, year, labels = null, filtreEntete = null, unit = 'kchf', kind = 'billing',
}) {
  const { t } = useTranslation();
  const p = useP();
  const MONTHS = useMemo(() => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)), [t]);
  const dispo = useMemo(() => [year - 1, year - 2].filter((yr) => (yr === year - 1 ? dataPrevYear : dataPrev2Year)?.length),
    [year, dataPrevYear, dataPrev2Year]);
  const [annees, setAnnees] = useState(() => new Set([year - 1, year - 2]));
  const retenues = dispo.filter((yr) => annees.has(yr));

  const donnees = useMemo(() => {
    if (!retenues.length) return null;
    const parAn = { [year - 1]: dataPrevYear || [], [year - 2]: dataPrev2Year || [] };
    const lignes = retenues.map((yr) => {
      const m = Array(12).fill(0);
      parAn[yr].forEach((r) => {
        if (labels && !labels.has(r.label)) return;              // hors des catégories retenues
        (r.monthly_values || []).forEach((v, i) => { m[i] += Number(v) || 0; });
      });
      return { yr, m };
    });
    const moy = Array(12).fill(0).map((_, i) => lignes.reduce((a, l) => a + l.m[i], 0) / lignes.length);
    const tot = moy.reduce((a, b) => a + b, 0);
    return { lignes, moy, tot };
  }, [retenues, year, dataPrevYear, dataPrev2Year, labels]); // eslint-disable-line

  const choixAnnees = dispo.length > 0 && (
    <SegmentedToggle multiple value={annees} options={dispo.map((yr) => ({ value: yr, label: String(yr) }))}
                     onChange={(next) => { if (next.size) setAnnees(next); }} />
  );
  const total = (vals) => vals.reduce((a, b) => a + b, 0);
  return (
    <CardShell title={kind === 'ca' ? p("Saisonnalité du chiffre d'affaires") : p('Saisonnalité de la facturation')} flush viewControls={choixAnnees || null}
               primaryAction={filtreEntete}>
      {!dispo.length
        ? <div className="sales-empty">{t('scenarii:billing.seasonality.emptyHistory')}</div>
        : (
          <div className="sales-bill-scroll lk-hscroll">
            <table className="lk-entry-table sales-bill-table sb-table">
              <colgroup><col style={{ width: 300 }} /><col style={{ width: 92 }} />{MONTHS.map((m) => <col key={m} style={{ width: 70 }} />)}</colgroup>
              <thead>
                <tr>
                  <th className="sb-lbl">{p('Années retenues')}</th>
                  <th className="sb-an">{t('scenarii:billing.seasonality.total')}</th>
                  {MONTHS.map((m, i) => <th key={m} className={i === 0 ? 'lk-sep' : ''}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {donnees && donnees.lignes.map((l) => (
                  <tr key={l.yr} className="sb-cat">
                    <td className="sb-lbl sb-lbl--lu">{kind === 'ca' ? `${p("Chiffre d'affaires")} ${l.yr}` : t('scenarii:billing.seasonality.billingYear', { year: l.yr })}</td>
                    <td className="sb-an">{formatCHF(total(l.m), unit)}</td>
                    {l.m.map((v, i) => <td key={i} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{formatCHF(v, unit)}</td>)}
                  </tr>
                ))}
                {donnees && (
                  <>
                    <tr className="sb-offre">
                      <td className="sb-lbl">{t('scenarii:billing.seasonality.monthlyAverage')}</td>
                      <td className="sb-an">{formatCHF(donnees.tot, unit)}</td>
                      {donnees.moy.map((v, i) => <td key={i} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{formatCHF(v, unit)}</td>)}
                    </tr>
                    <tr className="sb-cat">
                      <td className="sb-lbl sb-lbl--lu">{p("Part de l'année")}</td>
                      <td className="sb-an">100 %</td>
                      {donnees.moy.map((v, i) => <td key={i} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{donnees.tot ? pct(v / donnees.tot) : '–'}</td>)}
                    </tr>
                  </>
                )}
              </tbody>
              {donnees && (
                <tfoot>
                  <tr className="sb-total-row">
                    <td className="sb-lbl">{t('scenarii:billing.seasonality.seasonalIndex')}<span className="sb-px">{p('base 100')}</span></td>
                    <td className="sb-an" />
                    {donnees.moy.map((v, i) => <td key={i} className={i === 0 ? 'lk-sep' : ''}>{donnees.tot ? Math.round((1200 * v) / donnees.tot) : '–'}</td>)}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
    </CardShell>
  );
}
